/**
 * Personnel Domain - Composition Root
 *
 * Single entry point for all domain dependencies.
 * Implements the singleton pattern with explicit initialization.
 *
 * Flow:
 * 1. App/bootstrap.ts calls initPersonnelComposition(httpClient) once authenticated
 * 2. Hooks call getEmployeeUseCases() / getStoreUseCases() / getProfileUseCases() /
 *    getRoleUseCases() / getPersonnelStorage()
 * 3. Use cases are already composed with their dependencies
 *
 * @module @personnel/composition
 */

import type { AxiosInstance } from 'axios';
import type {
  PersonnelComposition,
  EmployeeUseCases,
  StoreUseCases,
  NotificationPreferencesUseCases,
  ProfileUseCases,
  RoleUseCases,
  TipsUseCases,
  PersonnelStorage,
} from '@personnel/composition/types';
import {
  createEmployeeUseCases,
  createStoreUseCases,
  createNotificationPreferencesUseCases,
  createProfileUseCases,
  createRoleUseCases,
  createTipsUseCases,
  createPersonnelStorage,
} from '@personnel/composition/factories';
import { removeEmployeeFilters } from '@personnel/infrastructure/storage/employeeFiltersStorage';

export type {
  PersonnelComposition,
  EmployeeUseCases,
  StoreUseCases,
  NotificationPreferencesUseCases,
  ProfileUseCases,
  RoleUseCases,
  TipsUseCases,
  PersonnelStorage,
} from '@personnel/composition/types';

/**
 * Creates a new complete composition of the domain.
 * Internal use — consumers should use the init/get functions.
 */
const createPersonnelComposition = (httpClient: AxiosInstance): PersonnelComposition => ({
  employeeUseCases: createEmployeeUseCases(httpClient),
  storeUseCases: createStoreUseCases(httpClient),
  notificationPreferencesUseCases: createNotificationPreferencesUseCases(httpClient),
  profileUseCases: createProfileUseCases(httpClient),
  roleUseCases: createRoleUseCases(httpClient),
  tipsUseCases: createTipsUseCases(httpClient),
  storage: createPersonnelStorage(),
});

let composition: PersonnelComposition | null = null;

/**
 * Initializes the Personnel domain Composition Root.
 * MUST be called ONCE during app bootstrap, BEFORE any hook uses the use cases.
 *
 * @param httpClient - Axios instance for the connect API
 *
 * @example
 * // In App/bootstrap.ts
 * import { connectApi } from 'utils/api/baas/axiosBase';
 * import { initPersonnelComposition } from '@personnel/composition';
 *
 * initPersonnelComposition(connectApi);
 */
export const initPersonnelComposition = (httpClient: AxiosInstance): void => {
  if (composition !== null) {
    return;
  }

  composition = createPersonnelComposition(httpClient);
};

/**
 * Gets the Composition Root singleton.
 * MUST be called AFTER initPersonnelComposition.
 * @throws Error if not initialized
 */
export const getPersonnelComposition = (): PersonnelComposition => {
  if (composition === null) {
    throw new Error(
      '[Personnel] Composition not initialized.\n' +
        'Call initPersonnelComposition(httpClient) in App/bootstrap.ts before using hooks.',
    );
  }

  return composition;
};

/**
 * Reset for testing.
 * @internal
 */
export const resetPersonnelComposition = (): void => {
  composition = null;
};

/**
 * Clears session-scoped personnel storage (e.g. persisted employee filters)
 * so the next session starts fresh. Safe to call without initialization —
 * it operates directly on browser storage and is intended for logout.
 */
export const clearPersonnelSessionStorage = (): void => {
  removeEmployeeFilters();
};

/** Gets employee use cases. */
export const getEmployeeUseCases = (): EmployeeUseCases => getPersonnelComposition().employeeUseCases;

/** Gets store use cases. */
export const getStoreUseCases = (): StoreUseCases => getPersonnelComposition().storeUseCases;

/** Gets the notification preferences use cases. */
export const getNotificationPreferencesUseCases = (): NotificationPreferencesUseCases =>
  getPersonnelComposition().notificationPreferencesUseCases;

/** Gets profile use cases. */
export const getProfileUseCases = (): ProfileUseCases => getPersonnelComposition().profileUseCases;

/** Gets role use cases. */
export const getRoleUseCases = (): RoleUseCases => getPersonnelComposition().roleUseCases;

/** Gets tips retention use cases. */
export const getTipsUseCases = (): TipsUseCases => getPersonnelComposition().tipsUseCases;

/** Gets the personnel storage service (session + filter accessors). */
export const getPersonnelStorage = (): PersonnelStorage => getPersonnelComposition().storage;
